import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * ReviewStrip — shows the three latest approved owner reviews on the
 * homepage, linking to /reviews. Renders nothing until real reviews exist,
 * keeping the trust story honest.
 */
export const ReviewStrip = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);

  const rows = (data?.rows || []).slice(0, 3);
  if (rows.length === 0) return null;
  const summary = data.summary;

  return (
    <section
      id="owner-reviews"
      data-testid="review-strip"
      className="relative py-16 sm:py-20 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">
              [ Owner reviews · human-approved ]
            </div>
            <h2 className="font-display text-2xl sm:text-4xl font-semibold leading-tight text-white">
              Rated {summary.average}/5 by{" "}
              <span className="brand-text-gradient">{summary.count} owner{summary.count === 1 ? "" : "s"}.</span>
            </h2>
          </div>
          <Link
            to="/reviews"
            data-testid="review-strip-all-link"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors shrink-0"
          >
            Read all reviews
            <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
              data-testid="review-strip-card"
            >
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="w-3.5 h-3.5"
                    strokeWidth={1.8}
                    style={{
                      color: i <= r.rating ? "#39FF14" : "#3f3f46",
                      fill: i <= r.rating ? "#39FF14" : "transparent",
                    }}
                  />
                ))}
              </div>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed line-clamp-4">{r.body}</p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-[13px] text-white font-medium">{r.name}</span>
                {r.verified_owner && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/[0.07] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-[#00E5FF]">
                    <ShieldCheck className="w-3 h-3" strokeWidth={2.2} /> Verified owner
                  </span>
                )}
                {r.vehicle && (
                  <span className="font-mono text-[10px] text-zinc-500">{r.vehicle}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
